import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {archiveApi} from '@api/archive/api';
import {transactionApi} from '@api/transaction/api';
import {getActiveAccountId} from '@utils/account';
import {getErrorMessage} from '@utils/requests/error';
import {getResponseBody} from '@utils/apiResponse';
import type {
    ArchiveFilterStatus,
    ArchiveRequestResource,
    ArchiveRequestTransactionResource,
    PaginatedResponse,
    StoreArchiveRequestRequest
} from '@api/archive/api.d';
import type {TransactionArchivePreviewResource} from '@api/transaction/api.d';
import type {ErrorMessage} from '@utils/requests/error.types';
import type {AppDispatch, RootState} from '@src/client/index';

/** How often the detail page polls while an archive request is still pending/processing — no server push exists for status changes. */
export const ARCHIVE_DETAIL_POLL_INTERVAL_MS = 2000;

/** Fixed page size sent explicitly (server supports per_page up to 100) so pagination math matches the server, not its default of 10. */
export const ARCHIVE_REQUESTS_PER_PAGE = 15;

/** Highest `per_page` the transactions endpoint accepts — used to walk the whole list for the CSV export. */
export const ARCHIVE_TRANSACTIONS_MAX_PER_PAGE = 100;

export interface ArchiveState {
    archiveRequests: ArchiveRequestResource[];
    archiveRequestsCount: number;
    page: number;
    statusFilter: string;
    isFetchingArchiveRequests: boolean;
    fetchingArchiveRequestsError: ErrorMessage | null;
    archiveRequest: ArchiveRequestResource | null;
    isFetchingArchiveRequest: boolean;
    fetchingArchiveRequestError: ErrorMessage | null;
    isMutatingArchiveRequest: boolean;
    isCreatingArchiveRequest: boolean;
    creatingArchiveRequestError: ErrorMessage | null;
    archiveRequestPreview: TransactionArchivePreviewResource | null;
    isFetchingArchiveRequestPreview: boolean;
    fetchingArchiveRequestPreviewError: ErrorMessage | null;
    archiveRequestTransactions: ArchiveRequestTransactionResource[];
    archiveRequestTransactionsCount: number;
    archiveRequestTransactionsPage: number;
    archiveRequestTransactionsPerPage: number;
    archiveRequestTransactionStatuses: ArchiveFilterStatus[];
    isFetchingArchiveRequestTransactions: boolean;
    fetchingArchiveRequestTransactionsError: ErrorMessage | null;
    /** Per-store fetch generations: the SSR process reuses this module for every request, so a shared module-level counter would let one request's fetch cancel another's. */
    archiveRequestsFetchGeneration: number;
    archiveRequestFetchGeneration: number;
    archiveRequestTransactionsFetchGeneration: number;
}

const initialState: ArchiveState = {
    archiveRequests: [],
    archiveRequestsCount: 0,
    page: 1,
    statusFilter: '',
    isFetchingArchiveRequests: false,
    fetchingArchiveRequestsError: null,
    archiveRequest: null,
    isFetchingArchiveRequest: false,
    fetchingArchiveRequestError: null,
    isMutatingArchiveRequest: false,
    isCreatingArchiveRequest: false,
    creatingArchiveRequestError: null,
    archiveRequestPreview: null,
    isFetchingArchiveRequestPreview: false,
    fetchingArchiveRequestPreviewError: null,
    archiveRequestTransactions: [],
    archiveRequestTransactionsCount: 0,
    archiveRequestTransactionsPage: 1,
    archiveRequestTransactionsPerPage: 10,
    archiveRequestTransactionStatuses: [],
    isFetchingArchiveRequestTransactions: false,
    fetchingArchiveRequestTransactionsError: null,
    archiveRequestsFetchGeneration: 0,
    archiveRequestFetchGeneration: 0,
    archiveRequestTransactionsFetchGeneration: 0
};

export const archiveSlice = createSlice({
    name: 'archive',
    initialState,
    reducers: {
        setArchiveRequests: (state, action: PayloadAction<ArchiveRequestResource[]>) => {
            state.archiveRequests = action.payload;
        },
        setArchiveRequestsCount: (state, action: PayloadAction<number>) => {
            state.archiveRequestsCount = action.payload;
        },
        setPage: (state, action: PayloadAction<number>) => {
            state.page = action.payload;
        },
        setStatusFilter: (state, action: PayloadAction<string>) => {
            state.statusFilter = action.payload;
        },
        setIsFetchingArchiveRequests: (state, action: PayloadAction<boolean>) => {
            state.isFetchingArchiveRequests = action.payload;
        },
        setFetchingArchiveRequestsError: (state, action: PayloadAction<ErrorMessage | null>) => {
            state.fetchingArchiveRequestsError = action.payload;
        },
        setArchiveRequest: (state, action: PayloadAction<ArchiveRequestResource | null>) => {
            state.archiveRequest = action.payload;
        },
        setIsFetchingArchiveRequest: (state, action: PayloadAction<boolean>) => {
            state.isFetchingArchiveRequest = action.payload;
        },
        setFetchingArchiveRequestError: (state, action: PayloadAction<ErrorMessage | null>) => {
            state.fetchingArchiveRequestError = action.payload;
        },
        setIsMutatingArchiveRequest: (state, action: PayloadAction<boolean>) => {
            state.isMutatingArchiveRequest = action.payload;
        },
        setIsCreatingArchiveRequest: (state, action: PayloadAction<boolean>) => {
            state.isCreatingArchiveRequest = action.payload;
        },
        setCreatingArchiveRequestError: (state, action: PayloadAction<ErrorMessage | null>) => {
            state.creatingArchiveRequestError = action.payload;
        },
        setArchiveRequestPreview: (state, action: PayloadAction<TransactionArchivePreviewResource | null>) => {
            state.archiveRequestPreview = action.payload;
        },
        setIsFetchingArchiveRequestPreview: (state, action: PayloadAction<boolean>) => {
            state.isFetchingArchiveRequestPreview = action.payload;
        },
        setFetchingArchiveRequestPreviewError: (state, action: PayloadAction<ErrorMessage | null>) => {
            state.fetchingArchiveRequestPreviewError = action.payload;
        },
        setArchiveRequestTransactions: (state, action: PayloadAction<ArchiveRequestTransactionResource[]>) => {
            state.archiveRequestTransactions = action.payload;
        },
        setArchiveRequestTransactionsCount: (state, action: PayloadAction<number>) => {
            state.archiveRequestTransactionsCount = action.payload;
        },
        setArchiveRequestTransactionsPage: (state, action: PayloadAction<number>) => {
            state.archiveRequestTransactionsPage = action.payload;
        },
        setArchiveRequestTransactionsPerPage: (state, action: PayloadAction<number>) => {
            state.archiveRequestTransactionsPerPage = action.payload;
        },
        setArchiveRequestTransactionStatuses: (state, action: PayloadAction<ArchiveFilterStatus[]>) => {
            state.archiveRequestTransactionStatuses = action.payload;
        },
        setIsFetchingArchiveRequestTransactions: (state, action: PayloadAction<boolean>) => {
            state.isFetchingArchiveRequestTransactions = action.payload;
        },
        setFetchingArchiveRequestTransactionsError: (state, action: PayloadAction<ErrorMessage | null>) => {
            state.fetchingArchiveRequestTransactionsError = action.payload;
        },
        /** Opening another archive must not inherit the previous one's page, filter or rows. */
        resetArchiveRequestTransactions: (state) => {
            state.archiveRequestTransactions = [];
            state.archiveRequestTransactionsCount = 0;
            state.archiveRequestTransactionsPage = 1;
            state.archiveRequestTransactionStatuses = [];
            state.fetchingArchiveRequestTransactionsError = null;
        },
        /** Editing a filter invalidates the previously fetched preview count; safe since the panel re-fetches it on every edit. */
        resetArchiveRequestPreview: (state) => {
            state.archiveRequestPreview = null;
            state.fetchingArchiveRequestPreviewError = null;
        },
        /** Clears state scoped to one submission attempt, called on panel dismiss or successful create, so reopening starts clean. */
        resetArchiveRequestForm: (state) => {
            state.archiveRequestPreview = null;
            state.fetchingArchiveRequestPreviewError = null;
            state.creatingArchiveRequestError = null;
        },
        setArchiveRequestsFetchGeneration: (state, action: PayloadAction<number>) => {
            state.archiveRequestsFetchGeneration = action.payload;
        },
        setArchiveRequestFetchGeneration: (state, action: PayloadAction<number>) => {
            state.archiveRequestFetchGeneration = action.payload;
        },
        setArchiveRequestTransactionsFetchGeneration: (state, action: PayloadAction<number>) => {
            state.archiveRequestTransactionsFetchGeneration = action.payload;
        }
    }
});

export const {
    setArchiveRequests,
    setArchiveRequestsCount,
    setPage,
    setStatusFilter,
    setIsFetchingArchiveRequests,
    setFetchingArchiveRequestsError,
    setArchiveRequest,
    setIsFetchingArchiveRequest,
    setFetchingArchiveRequestError,
    setIsMutatingArchiveRequest,
    setIsCreatingArchiveRequest,
    setCreatingArchiveRequestError,
    setArchiveRequestPreview,
    setIsFetchingArchiveRequestPreview,
    setFetchingArchiveRequestPreviewError,
    resetArchiveRequestPreview,
    resetArchiveRequestForm,
    setArchiveRequestTransactions,
    setArchiveRequestTransactionsCount,
    setArchiveRequestTransactionsPage,
    setArchiveRequestTransactionsPerPage,
    setArchiveRequestTransactionStatuses,
    setIsFetchingArchiveRequestTransactions,
    setFetchingArchiveRequestTransactionsError,
    resetArchiveRequestTransactions,
    setArchiveRequestsFetchGeneration,
    setArchiveRequestFetchGeneration,
    setArchiveRequestTransactionsFetchGeneration
} = archiveSlice.actions;

export interface FetchArchiveRequestsOptions {
    headers?: Record<string, string>;
    timeout?: number;
}

/** accessToken is read from the store, not an option — SSR needs it explicit since httpClient attaches it client-side only. */
export const fetchArchiveRequests = (
    options: FetchArchiveRequestsOptions = {}
) => async (dispatch: AppDispatch, getState: () => RootState): Promise<boolean> => {
    const {headers, timeout} = options;
    const state = getState();
    const {page, statusFilter} = state.archive;
    const accessToken = state.user?.accessToken ?? undefined;
    /** Guards against a superseded response overwriting a newer request; the generation lives in the store so SSR requests stay isolated. */
    const fetchGeneration = state.archive.archiveRequestsFetchGeneration + 1;
    dispatch(setArchiveRequestsFetchGeneration(fetchGeneration));

    dispatch(setIsFetchingArchiveRequests(true));
    dispatch(setFetchingArchiveRequestsError(null));

    try {
        const response = await archiveApi.getArchiveRequests({
            params: {page, per_page: ARCHIVE_REQUESTS_PER_PAGE, status: statusFilter || undefined},
            accessToken,
            headers,
            timeout
        }) as unknown as PaginatedResponse<ArchiveRequestResource>;
        if (fetchGeneration === getState().archive.archiveRequestsFetchGeneration) {
            dispatch(setArchiveRequests(response.data));
            dispatch(setArchiveRequestsCount(response.meta.total));
            dispatch(setIsFetchingArchiveRequests(false));
        }
        return true;
    } catch (err: unknown) {
        if (fetchGeneration === getState().archive.archiveRequestsFetchGeneration) {
            dispatch(setFetchingArchiveRequestsError(getErrorMessage(err)));
            dispatch(setIsFetchingArchiveRequests(false));
        }
        return false;
    }
};

export const selectArchiveRequests = (state: RootState): ArchiveRequestResource[] => state.archive.archiveRequests;
export const selectArchiveRequestsCount = (state: RootState): number => state.archive.archiveRequestsCount;
export const selectArchiveRequestsPage = (state: RootState): number => state.archive.page;
export const selectArchiveStatusFilter = (state: RootState): string => state.archive.statusFilter;
export const selectIsFetchingArchiveRequests = (state: RootState): boolean => state.archive.isFetchingArchiveRequests;
export const selectFetchingArchiveRequestsError = (state: RootState): ErrorMessage | null => state.archive.fetchingArchiveRequestsError;

export interface FetchArchiveRequestOptions {
    headers?: Record<string, string>;
    timeout?: number;
}

/** accessToken is read from the store rather than accepted as an option, same reasoning as fetchArchiveRequests above. */
export const fetchArchiveRequest = (
    archiveRequestId: string,
    options: FetchArchiveRequestOptions = {}
) => async (dispatch: AppDispatch, getState: () => RootState): Promise<boolean> => {
    const {headers, timeout} = options;
    const accessToken = getState().user?.accessToken ?? undefined;
    /** Per-store generation guard against superseded responses; see fetchArchiveRequests. */
    const fetchGeneration = getState().archive.archiveRequestFetchGeneration + 1;
    dispatch(setArchiveRequestFetchGeneration(fetchGeneration));

    dispatch(setIsFetchingArchiveRequest(true));
    dispatch(setFetchingArchiveRequestError(null));

    try {
        const response = await archiveApi.getArchiveRequest({
            archiveRequestId, accessToken, headers, timeout
        });
        if (fetchGeneration === getState().archive.archiveRequestFetchGeneration) {
            dispatch(setArchiveRequest(getResponseBody<ArchiveRequestResource>(response)));
            dispatch(setIsFetchingArchiveRequest(false));
        }
        return true;
    } catch (err: unknown) {
        if (fetchGeneration === getState().archive.archiveRequestFetchGeneration) {
            dispatch(setFetchingArchiveRequestError(getErrorMessage(err)));
            dispatch(setIsFetchingArchiveRequest(false));
        }
        return false;
    }
};

export const deleteArchiveRequest = (
    archiveRequestId: string
) => async (dispatch: AppDispatch, getState: () => RootState): Promise<boolean> => {
    const accessToken = getState().user?.accessToken ?? undefined;

    dispatch(setIsMutatingArchiveRequest(true));
    dispatch(setFetchingArchiveRequestError(null));

    try {
        await archiveApi.deleteArchiveRequest({archiveRequestId, accessToken});
        dispatch(setIsMutatingArchiveRequest(false));
        return true;
    } catch (err: unknown) {
        dispatch(setFetchingArchiveRequestError(getErrorMessage(err)));
        dispatch(setIsMutatingArchiveRequest(false));
        return false;
    }
};

export interface FetchArchiveRequestTransactionsOptions {
    headers?: Record<string, string>;
    timeout?: number;
}

/** Guards fetchArchiveRequestTransactions: a superseded response must not overwrite state committed by a newer request. */
/** Page, page size and status filter come from the store so polling re-fetches whatever the user is looking at right now. */
export const fetchArchiveRequestTransactions = (
    archiveRequestId: string,
    options: FetchArchiveRequestTransactionsOptions = {}
) => async (dispatch: AppDispatch, getState: () => RootState): Promise<boolean> => {
    const {headers, timeout} = options;
    const state = getState();
    const {
        archiveRequestTransactionsPage: page,
        archiveRequestTransactionsPerPage: perPage,
        archiveRequestTransactionStatuses: statuses
    } = state.archive;
    const accessToken = state.user?.accessToken ?? undefined;
    /** Per-store generation guard against superseded responses; see fetchArchiveRequests. */
    const fetchGeneration = state.archive.archiveRequestTransactionsFetchGeneration + 1;
    dispatch(setArchiveRequestTransactionsFetchGeneration(fetchGeneration));

    dispatch(setIsFetchingArchiveRequestTransactions(true));
    dispatch(setFetchingArchiveRequestTransactionsError(null));

    try {
        const response = await archiveApi.getArchiveRequestTransactions({
            archiveRequestId,
            params: {
                page,
                per_page: perPage,
                transaction_status: statuses.length > 0 ? statuses : undefined
            },
            accessToken,
            headers,
            timeout
        }) as unknown as PaginatedResponse<ArchiveRequestTransactionResource>;
        if (fetchGeneration === getState().archive.archiveRequestTransactionsFetchGeneration) {
            dispatch(setArchiveRequestTransactions(response.data));
            dispatch(setArchiveRequestTransactionsCount(response.meta.total));
            dispatch(setIsFetchingArchiveRequestTransactions(false));
        }
        return true;
    } catch (err: unknown) {
        if (fetchGeneration === getState().archive.archiveRequestTransactionsFetchGeneration) {
            dispatch(setFetchingArchiveRequestTransactionsError(getErrorMessage(err)));
            dispatch(setIsFetchingArchiveRequestTransactions(false));
        }
        return false;
    }
};

/** Walks every page at max page size without touching the store — the CSV covers the whole archive, not just the paged table. */
export const fetchAllArchiveRequestTransactions = (
    archiveRequestId: string
) => async (dispatch: AppDispatch, getState: () => RootState): Promise<ArchiveRequestTransactionResource[] | null> => {
    const accessToken = getState().user?.accessToken ?? undefined;
    const transactions: ArchiveRequestTransactionResource[] = [];
    let page = 1;
    let lastPage = 1;

    try {
        do {
            /* eslint-disable no-await-in-loop */
            const response = await archiveApi.getArchiveRequestTransactions({
                archiveRequestId,
                params: {page, per_page: ARCHIVE_TRANSACTIONS_MAX_PER_PAGE},
                accessToken
            }) as unknown as PaginatedResponse<ArchiveRequestTransactionResource>;
            /* eslint-enable no-await-in-loop */
            transactions.push(...response.data);
            lastPage = response.meta.last_page;
            page += 1;
        } while (page <= lastPage);

        return transactions;
    } catch (err: unknown) {
        dispatch(setFetchingArchiveRequestTransactionsError(getErrorMessage(err)));
        return null;
    }
};

export const selectArchiveRequestTransactions = (
    state: RootState
): ArchiveRequestTransactionResource[] => state.archive.archiveRequestTransactions;
export const selectArchiveRequestTransactionsCount = (state: RootState): number => state.archive.archiveRequestTransactionsCount;
export const selectArchiveRequestTransactionsPage = (state: RootState): number => state.archive.archiveRequestTransactionsPage;
export const selectArchiveRequestTransactionsPerPage = (state: RootState): number => state.archive.archiveRequestTransactionsPerPage;
export const selectArchiveRequestTransactionStatuses = (
    state: RootState
): ArchiveFilterStatus[] => state.archive.archiveRequestTransactionStatuses;
export const selectIsFetchingArchiveRequestTransactions = (
    state: RootState
): boolean => state.archive.isFetchingArchiveRequestTransactions;
export const selectFetchingArchiveRequestTransactionsError = (
    state: RootState
): ErrorMessage | null => state.archive.fetchingArchiveRequestTransactionsError;

export const selectArchiveRequest = (state: RootState): ArchiveRequestResource | null => state.archive.archiveRequest;
export const selectIsFetchingArchiveRequest = (state: RootState): boolean => state.archive.isFetchingArchiveRequest;
export const selectFetchingArchiveRequestError = (state: RootState): ErrorMessage | null => state.archive.fetchingArchiveRequestError;
export const selectIsMutatingArchiveRequest = (state: RootState): boolean => state.archive.isMutatingArchiveRequest;

/** accessToken is read from the store rather than accepted as an option, same reasoning as fetchArchiveRequests above. */
export const createArchiveRequest = (
    data: StoreArchiveRequestRequest
) => async (dispatch: AppDispatch, getState: () => RootState): Promise<ArchiveRequestResource | null> => {
    const accessToken = getState().user?.accessToken ?? undefined;

    dispatch(setIsCreatingArchiveRequest(true));
    dispatch(setCreatingArchiveRequestError(null));

    try {
        const response = await archiveApi.createArchiveRequest({data, accessToken});
        const archiveRequest = getResponseBody<ArchiveRequestResource>(response);
        dispatch(setIsCreatingArchiveRequest(false));
        return archiveRequest;
    } catch (err: unknown) {
        dispatch(setCreatingArchiveRequestError(getErrorMessage(err)));
        dispatch(setIsCreatingArchiveRequest(false));
        return null;
    }
};

/** accessToken is read from the store rather than accepted as an option, same reasoning as fetchArchiveRequests above. */
export const fetchArchiveRequestPreview = (
    data: StoreArchiveRequestRequest
) => async (dispatch: AppDispatch, getState: () => RootState): Promise<TransactionArchivePreviewResource | null> => {
    const state = getState();
    const accountId = getActiveAccountId(state.user);
    const accessToken = state.user?.accessToken ?? undefined;

    dispatch(setIsFetchingArchiveRequestPreview(true));
    dispatch(setFetchingArchiveRequestPreviewError(null));

    try {
        const response = await transactionApi.getTransactionsArchivePreview({
            accountId,
            data,
            accessToken
        });
        const preview = getResponseBody<TransactionArchivePreviewResource>(response);
        dispatch(setArchiveRequestPreview(preview));
        dispatch(setIsFetchingArchiveRequestPreview(false));
        return preview;
    } catch (err: unknown) {
        dispatch(setFetchingArchiveRequestPreviewError(getErrorMessage(err)));
        dispatch(setIsFetchingArchiveRequestPreview(false));
        return null;
    }
};

export const selectIsCreatingArchiveRequest = (state: RootState): boolean => state.archive.isCreatingArchiveRequest;
export const selectCreatingArchiveRequestError = (state: RootState): ErrorMessage | null => state.archive.creatingArchiveRequestError;
export const selectArchiveRequestPreview = (
    state: RootState
): TransactionArchivePreviewResource | null => state.archive.archiveRequestPreview;
export const selectIsFetchingArchiveRequestPreview = (state: RootState): boolean => state.archive.isFetchingArchiveRequestPreview;
export const selectFetchingArchiveRequestPreviewError = (
    state: RootState
): ErrorMessage | null => state.archive.fetchingArchiveRequestPreviewError;

export default archiveSlice.reducer;
